import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Hammer, Users, Calendar, Shield, ArrowRight, ChevronRight, CloudCog, Wrench, Leaf, UtensilsCrossed, Brush, Tent, Car, PaintBucket, Baseline as Pipeline, Zap, Ruler, HardHat, Construction, Sparkles, Package, Trees } from 'lucide-react';
import Button from '../components/ui/Button';
import ToolCard from '../components/features/ToolCard';
import { useQuery } from '@apollo/client';
import { GET_CATEGORIES } from '../graphql/queries/categories';
import { GET_FEATURED_TOOLS } from '../graphql/queries/featuredTools';
import { Category } from '../types/Category';

// Function to determine icon based on category name
const getCategoryIcon = (name: string, code: string, className: string = "h-5 w-5"): JSX.Element => {
  const nameLower = name.toLowerCase();
  
  // Check predefined mappings first
  const codeIconMap: Record<string, JSX.Element> = {
    'power-tools': <Wrench className={className} />,
    'hand-tools': <Hammer className={className} />,
    'garden-lawn': <Leaf className={className} />,
    'kitchen': <UtensilsCrossed className={className} />,
    'cleaning': <Brush className={className} />,
    'outdoor': <Tent className={className} />,
    'automotive': <Car className={className} />,
    'painting': <PaintBucket className={className} />,
    'plumbing': <Pipeline className={className} />,
    'electrical': <Zap className={className} />,
  };
  
  if (codeIconMap[code]) {
    return codeIconMap[code];
  }
  
  // Intelligent icon matching based on keywords
  if (nameLower.includes('have') || nameLower.includes('udendørs')) {
    return <Trees className={className} />; // Garden = Trees
  }
  if (nameLower.includes('el-') || nameLower.includes('elektrisk')) {
    return <Zap className={className} />; // Electrical = Zap
  }
  if (nameLower.includes('murer') || nameLower.includes('beton')) {
    return <HardHat className={className} />; // Masonry = Hard hat
  }
  if (nameLower.includes('tømrer') || nameLower.includes('byg')) {
    return <HardHat className={className} />; // Carpentry = Hard hat
  }
  if (nameLower.includes('rengøring') || nameLower.includes('oprydning')) {
    return <Sparkles className={className} />; // Cleaning = Sparkles
  }
  if (nameLower.includes('måle')) {
    return <Ruler className={className} />; // Measuring = Ruler
  }
  if (nameLower.includes('lift') || nameLower.includes('stillads')) {
    return <Construction className={className} />; // Lift/scaffolding = Construction
  }
  if (nameLower.includes('hånd')) {
    return <Hammer className={className} />; // Hand tools = Hammer
  }
  if (nameLower.includes('bil') || nameLower.includes('automotive')) {
    return <Car className={className} />; // Automotive = Car
  }
  if (nameLower.includes('maling') || nameLower.includes('maler')) {
    return <PaintBucket className={className} />; // Painting = Paint bucket
  }
  if (nameLower.includes('vvs') || nameLower.includes('rør')) {
    return <Pipeline className={className} />; // Plumbing = Pipeline
  }
  if (nameLower.includes('køkken')) {
    return <UtensilsCrossed className={className} />; // Kitchen = Utensils
  }
  if (nameLower.includes('forbrugs')) {
    return <Package className={className} />; // Consumables = Package
  }
  
  // Default fallback
  return <Wrench className={className} />;
};

// Function to determine color based on category name (returns text color class)
const getCategoryTextColor = (name: string, code: string): string => {
  const nameLower = name.toLowerCase();
  
  // Check predefined mappings first
  const codeColorMap: Record<string, string> = {
    'power-tools': 'text-blue-400',
    'hand-tools': 'text-orange-400',
    'garden-lawn': 'text-green-400',
    'kitchen': 'text-red-400',
    'cleaning': 'text-purple-400',
    'outdoor': 'text-green-400',
    'automotive': 'text-gray-400',
    'painting': 'text-pink-400',
    'plumbing': 'text-indigo-400',
    'electrical': 'text-blue-300',
  };
  
  if (codeColorMap[code]) {
    return codeColorMap[code];
  }
  
  // Intelligent color matching based on keywords
  if (nameLower.includes('have') || nameLower.includes('plæne') || nameLower.includes('udendørs')) {
    return 'text-green-400';
  }
  if (nameLower.includes('el-') || nameLower.includes('elektrisk')) {
    return 'text-blue-400';
  }
  if (nameLower.includes('murer') || nameLower.includes('beton') || nameLower.includes('cement')) {
    return 'text-gray-400';
  }
  if (nameLower.includes('tømrer') || nameLower.includes('byg') || nameLower.includes('træ')) {
    return 'text-amber-400';
  }
  if (nameLower.includes('rengøring') || nameLower.includes('oprydning')) {
    return 'text-purple-400';
  }
  if (nameLower.includes('måle') || nameLower.includes('præcision')) {
    return 'text-blue-400';
  }
  if (nameLower.includes('lift') || nameLower.includes('stillads') || nameLower.includes('stige')) {
    return 'text-yellow-400';
  }
  if (nameLower.includes('hånd')) {
    return 'text-orange-400';
  }
  if (nameLower.includes('bil') || nameLower.includes('automotive')) {
    return 'text-gray-400';
  }
  if (nameLower.includes('maling') || nameLower.includes('maler')) {
    return 'text-pink-400';
  }
  if (nameLower.includes('vvs') || nameLower.includes('rør')) {
    return 'text-indigo-400';
  }
  if (nameLower.includes('køkken')) {
    return 'text-red-400';
  }
  if (nameLower.includes('forbrugs')) {
    return 'text-slate-400';
  }
  
  // Default fallback
  return 'text-gray-400';
};

const Home: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState<Category[]>([]);
  const { data: catData } = useQuery(GET_CATEGORIES);
  const { data: toolsData } = useQuery(GET_FEATURED_TOOLS, {
    variables: {
      date: new Date().toISOString().split('T')[0]
    }
  });

  useEffect(() => {
    if (catData) {
      setCategories(catData.categories);
    }
  }, [catData])

  const featuredTools = toolsData?.featuredTools?.items?.map((tool: any) => ({
    ...tool,
    pricePerDay: tool.pricePerDayCents / 100
  })) || [];

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-green-600 to-green-700 text-white">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="container mx-auto px-4 py-16 sm:py-24 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl mb-6">
              Lån det værktøj, du har brug for – når du har brug for det
            </h1>
            <p className="text-lg sm:text-xl text-green-100 mb-8">
              DoMore forbinder folk, der mangler værktøj, med dem der har det. Spar penge, reducer spild og styrk fællesskabet – ét værktøj ad gangen.
            </p>
            
            {/* Search Bar */}
            <div className="relative max-w-xl">
              <div className="flex hadow-lg overflow-hidden bg-white" style={{borderRadius: "6px", overflow: "visible"}}>
                <div className="flex-grow">
                  <input
                    type="text"
                    placeholder="Hvad leder du efter?"
                    className="w-full px-4 py-3 text-gray-900 focus:outline-none"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <div>
                  <Link to={`/browse${searchQuery ? `?q=${searchQuery}` : ''}`}>
                    <Button variant="primary" size="lg" style={{borderRadius: "0", borderTopRightRadius: "6px", borderBottomRightRadius: "6px", position: "relative", left: 2}}>
                      <Search className="h-5 w-5 mr-2" />
                      Søg
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
            
            {/* Quick Categories */}
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-2">
              {categories.slice(0, 3).map((category) => {
                const iconColor = getCategoryTextColor(category.name, category.code);
                
                return (
                  <Link 
                    key={category.name}
                    to={`/browse?category_ids=${category.id}`}
                    className="bg-white bg-opacity-10 rounded-lg p-3 hover:bg-opacity-20 transition-colors duration-200"
                  >
                    <div className="flex items-center">
                      <span className={`mr-2 ${iconColor}`}>{getCategoryIcon(category.name, category.code, "h-5 w-5")}</span>
                      <span>{category.name}</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>
      
      {/* How It Works */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Sådan virker det</h2>
            <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
              At låne og udlåne værktøj har aldrig været nemmere
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <Search className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">Find værktøj</h3>
              <p className="text-gray-600">
                Gennemse tusindvis af værktøj i dit område og find præcis det, du har brug for.
              </p>
            </div>
            
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <Calendar className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">Book & betal</h3>
              <p className="text-gray-600">
                Reservér værktøj til de datoer, du har brug for – og betal sikkert via vores platform.
              </p>
            </div>
            
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <Hammer className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">Brug & aflever</h3>
              <p className="text-gray-600">
                Hent værktøj, fuldfør dit projekt, og aflever det til tiden for at opbygge tillid.
              </p>
            </div>
          </div>
          
          <div className="text-center mt-12">
            <Link to="/how-it-works">
              <Button variant="outline" icon={<ArrowRight className="h-4 w-4 ml-2" />}>
                Læs mere
              </Button>
            </Link>
          </div>
        </div>
      </section>
      
      {/* Featured Tools */}
      <section className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900">Fremhævet værktøj</h2>
            <Link to="/browse" className="text-green-600 hover:text-green-700 flex items-center">
              <span>Se alle</span>
              <ChevronRight className="h-4 w-4 ml-1" />
            </Link>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredTools.slice(0, 6).map((tool) => (
              <ToolCard key={tool.id} tool={tool} featured={true} />
            ))}
          </div>
        </div>
      </section>
      
      {/* Benefits Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Hvorfor bruge DoMore?</h2>
            <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
              Bliv en del af tusindvis af mennesker der sparer penge og minimerer spild
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <Users className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">Skab fællesskab</h3>
              <p className="text-gray-600">
                Skab relationer i dit nærområde ved at dele ressourcer med hinanden.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">Spar penge</h3>
              <p className="text-gray-600">
                Lån det værktøj du har brug for – i stedet for at købe noget du sjældent bruger.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">Reducer spild</h3>
              <p className="text-gray-600">
                Hjælp miljøet ved at dele udstyr frem for at alle køber deres eget.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <Shield className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">Sikkert og trygt</h3>
              <p className="text-gray-600">
                Vores verifikations- og anmeldelsessystem sikrer trygge udlån.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">Nemt og enkelt</h3>
              <p className="text-gray-600">
                Vores platform gør det nemt at finde, booke og returnere værktøj – uden bøvl.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">Ekstra indtægt</h3>
              <p className="text-gray-600">
                Tjen penge på at udlåne værktøj, der bare samler støv i din garage.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-16 bg-green-600 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-4">Klar til at komme i gang?</h2>
            <p className="text-xl text-green-100 mb-8">
              Bliv en del af vores fællesskab af værktøjsdelere i dag og spar penge, mens du hjælper miljøet.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to='/dashboard'>
                <Button variant="secondary" size="lg" style={{height: "50px"}}>
                  Udlån dit værktøj
                </Button>
              </Link>
              <Link to='/browse'>
                <Button
                  variant="outline"
                  size="lg"
                  className="border-white hover:text-white hover:bg-white hover:bg-opacity-10"
                  style={{height: "50px"}}
                >
                  Find værktøj
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;